import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate, AnimatePresence } from "framer-motion";
import {
  Stethoscope, Calendar, Users, Receipt, FileText,
  MessageCircle, Globe, BarChart3, ShieldCheck,
  CheckCircle2, ArrowRight, Star, Quote,
  ChevronRight, Twitter, Linkedin, Github, Mail, ExternalLink, Clock,
} from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import Logo from "../components/Logo";
import PageMeta from "../components/PageMeta";

/* ─── FONTS ─────────────────────────────────────────────── */
// Add to your index.html or global CSS:
// <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap" rel="stylesheet"/>

/* ─── TOKENS ─────────────────────────────────────────────── */
const T = {
  bg:      "#eef5ff",
  surface: "rgba(255,255,255,0.56)",
  card:    "rgba(255,255,255,0.72)",
  border:  "rgba(27,110,243,0.14)",
  blue:    "#1769E8",
  blueD:   "#0F56C7",
  amber:   "#D98B00",
  muted:   "#6B7B93",
  dim:     "#B8C7DC",
  text:    "#10213D",
  sub:     "#5D6D84",
};

/* ─── BACKGROUND ─────────────────────────────────────────── */
function Background() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="absolute inset-0" style={{ background: T.bg }} />
      <div className="absolute inset-0 opacity-60" style={{
        backgroundImage: `linear-gradient(rgba(27,110,243,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(27,110,243,0.045) 1px, transparent 1px)`,
        backgroundSize: "64px 64px",
        maskImage: "radial-gradient(ellipse 90% 70% at 50% 0%, black, transparent 75%)",
      }} />
      <div className="absolute -top-48 -left-40 h-[620px] w-[620px] rounded-full" style={{
        background: "radial-gradient(circle, rgba(104,174,255,0.34), transparent 68%)",
        filter: "blur(35px)",
      }} />
      <div className="absolute top-[28%] -right-52 h-[560px] w-[560px] rounded-full" style={{
        background: "radial-gradient(circle, rgba(188,226,255,0.58), transparent 68%)",
        filter: "blur(45px)",
      }} />
      <motion.div animate={{ x: [0, 35, 0], y: [0, -24, 0], scale: [1, 1.08, 1] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-[-260px] left-[32%] h-[620px] w-[620px] rounded-full" style={{
        background: "radial-gradient(circle, rgba(92,145,255,0.18), transparent 68%)",
        filter: "blur(55px)",
      }} />
    </div>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return <motion.div className="scroll-progress" style={{ scaleX }} aria-hidden="true" />;
}

/* ─── NAVBAR ─────────────────────────────────────────────── */
function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const headerOpacity = useTransform(scrollY, [0, 80], [0, 1]);
  const headerY = useTransform(scrollY, [0, 80], [0, -2]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener("resize", close, { passive: true });
    return () => window.removeEventListener("resize", close);
  }, [menuOpen]);
  return (
    <motion.header
      className="fixed top-0 inset-x-0 z-50"
      style={{ padding: "14px 0", y: headerY }}
    >
      <motion.div className="absolute inset-0 -z-10 border-b" style={{ opacity: headerOpacity, background: `${T.bg}d9`, borderColor: T.border, backdropFilter: "blur(18px) saturate(150%)", WebkitBackdropFilter: "blur(18px) saturate(150%)" }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={28} />
          <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 17, color: T.text }}>
            HeDesk
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {[["#features","Features"],["#pricing","Pricing"],["#reviews","Reviews"]].map(([h,l])=>(
            <a key={h} href={h} style={{ color: T.sub, fontSize: 14, fontFamily: "'DM Sans',sans-serif" }}
              className="hover:text-white transition-colors">{l}</a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-4">
          <Link to="/login" style={{ color: T.sub, fontSize: 14, fontFamily: "'DM Sans',sans-serif" }}
            className="hover:text-white transition-colors">Log in</Link>
          <LiquidGlassButton to="/signup" className="nav-button">Get started</LiquidGlassButton>
        </div>
        <button
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(v => !v)}
          className="mobile-menu-trigger liquid-link"
        >
          <span className={`menu-icon ${menuOpen ? "is-open" : ""}`} aria-hidden="true"><i/><i/></span>
        </button>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="mobile-menu"
          >
            {[['#features','Features'],['#pricing','Pricing'],['#reviews','Reviews']].map(([h,l], i) => (
              <motion.a
                key={h}
                href={h}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => setMenuOpen(false)}
              >{l}</motion.a>
            ))}
            <Link to="/login" onClick={() => setMenuOpen(false)}>Log in</Link>
            <LiquidGlassButton to="/signup" className="mobile-menu-cta" onClick={() => setMenuOpen(false)}>Get started</LiquidGlassButton>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

/* ─── COUNTER ────────────────────────────────────────────── */
function CountUp({ to, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, { duration: 1.6, ease: "easeOut", onUpdate: n => setV(Math.floor(n)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{prefix}{v.toLocaleString("en-IN")}{suffix}</span>;
}

/* ─── MOTION + LIQUID GLASS BUTTONS ─────────────────────── */
function Reveal({ children, className = "", delay = 0, y = 28 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-90px 0px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.75, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function LiquidGlassButton({ children, to, href, variant = "primary", className = "", icon = null, onClick, disabled = false }) {
  const content = (
    <motion.button
      type="button"
      whileHover={{ y: -3, scale: 1.015 }}
      whileTap={{ scale: 0.975 }}
      transition={{ type: "spring", stiffness: 420, damping: 24 }}
      className={`liquid-button liquid-button-${variant} ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="liquid-button-shine" aria-hidden="true" />
      <span className="liquid-button-content">{children}{icon}</span>
    </motion.button>
  );
  if (to) return <Link to={to} className="inline-flex">{content}</Link>;
  if (href) return <a href={href} className="inline-flex">{content}</a>;
  return content;
}

/* ─── HERO ───────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="hero-section relative overflow-visible px-4 sm:px-6">
      <div className="hero-square-glow" aria-hidden="false" />
      <div className="max-w-6xl mx-auto hero-inner">
        <motion.div
          className="hero-copy"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="hero-eyebrow">
            <span className="hero-eyebrow-line" />
            <span>Built for Indian clinics</span>
            <span className="hero-eyebrow-line" />
          </div>

          <h1>
            Run your clinic.<br />
            <span className="hero-script">Stop managing</span><br />
            the paperwork.
          </h1>

          <p>
            Appointments, patients, billing, prescriptions and WhatsApp reminders, all in one system your receptionist can learn in a day.
          </p>

          <div className="hero-actions">
            <LiquidGlassButton to="/signup" icon={<ArrowRight size={16}/>}>Start free</LiquidGlassButton>
            <a href="#features" className="liquid-link">
              See all features <ChevronRight size={14}/>
            </a>
          </div>

          <div className="hero-proof">
            <div className="proof-avatars">
              {[
                "linear-gradient(135deg,#1769E8,#7BB7FF)",
                "linear-gradient(135deg,#5B67E8,#A8B1FF)",
                "linear-gradient(135deg,#D98B00,#FFD27A)",
                "linear-gradient(135deg,#0B9B72,#73E0BD)",
              ].map((background, i) => <span key={i} style={{ background }} />)}
            </div>
            <div>
              <strong>80+ clinics</strong>
              <small>across Northeast India</small>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="hero-dashboard"
          initial={{ opacity: 0, y: 80, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -8, scale: 1.008 }}
          style={{ transformPerspective: 1200 }}
        >
          <DashboardCard />
          <div className="dashboard-floor-glow" aria-hidden="true" />
        </motion.div>
      </div>
    </section>
  );
}

/* ─── DASHBOARD CARD ─────────────────────────────────────── */
function DashboardCard() {
  return (
    <div className="glass-card float-soft" style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 24,
      overflow: "hidden",
      boxShadow: `0 40px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px ${T.border}`,
    }}>
      {/* Title bar */}
      <div style={{ padding:"12px 16px",borderBottom:`1px solid ${T.border}`,background:T.surface,display:"flex",alignItems:"center",gap:8 }}>
        <div style={{ width:10,height:10,borderRadius:"50%",background:"#ef4444",opacity:0.7 }}/>
        <div style={{ width:10,height:10,borderRadius:"50%",background:"#f59e0b",opacity:0.7 }}/>
        <div style={{ width:10,height:10,borderRadius:"50%",background:"#10b981",opacity:0.7 }}/>
        <div style={{ marginLeft:8,padding:"3px 10px",borderRadius:5,background:"rgba(255,255,255,0.05)",fontFamily:"'Inter',monospace",fontSize:11,color:T.muted }}>
          app.hedesk.help/dashboard
        </div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"1fr 2fr",minHeight:380 }}>
        {/* Sidebar */}
        <div style={{ borderRight:`1px solid ${T.border}`,padding:16,background:T.surface }}>
          {["Dashboard","Appointments","Queue","Patients","Billing","Prescriptions","Reports"].map((l,i)=>(
            <div key={l} style={{
              padding:"8px 10px",borderRadius:6,fontSize:12,marginBottom:2,
              fontFamily:"'DM Sans',sans-serif",
              background: i===0 ? `${T.blue}20` : "transparent",
              color: i===0 ? T.text : T.muted,
              fontWeight: i===0 ? 600 : 400,
            }}>{l}</div>
          ))}
        </div>

        {/* Main */}
        <div style={{ padding:20 }}>
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:700,fontSize:16,color:T.text,marginBottom:3 }}>
            Good morning, Dr. Anjali
          </div>
          <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:T.muted,marginBottom:16 }}>
            Friday · 14 clinics scheduled today
          </div>

          {/* KPI row */}
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16 }}>
            {[
              ["Today","14 appts","#1B6EF3"],
              ["Revenue","₹1.03L","#10B981"],
              ["Queue","4 waiting","#F59E0B"],
            ].map(([k,v,c])=>(
              <div key={k} style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:10,padding:"10px 12px" }}>
                <div style={{ fontSize:10,color:T.muted,fontFamily:"'DM Sans',sans-serif",marginBottom:4 }}>{k}</div>
                <div style={{ fontSize:14,fontWeight:700,fontFamily:"'Plus Jakarta Sans',sans-serif",color:c }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Bar chart */}
          <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,padding:14,marginBottom:12 }}>
            <div style={{ fontSize:11,color:T.muted,fontFamily:"'DM Sans',sans-serif",marginBottom:10 }}>Weekly revenue</div>
            <div style={{ display:"flex",alignItems:"flex-end",gap:5,height:64 }}>
              {[35,55,45,78,65,90,82].map((h,i)=>(
                <div key={i} style={{ flex:1,borderRadius:"3px 3px 0 0",height:`${h}%`,
                  background: i===5 ? T.blue : `${T.blue}40` }}/>
              ))}
            </div>
          </div>

          {/* Next appointments */}
          <div style={{ fontSize:11,color:T.muted,fontFamily:"'DM Sans',sans-serif",marginBottom:8 }}>Next up</div>
          {[["09:00","Priya Patel","checked-in"],["10:30","Rahul Singh","scheduled"]].map(([t,n,s])=>(
            <div key={t} style={{ display:"flex",alignItems:"center",gap:8,padding:"7px 10px",
              background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,marginBottom:5 }}>
              <div style={{ fontFamily:"'Inter',monospace",fontSize:10,color:T.muted,width:32 }}>{t}</div>
              <div style={{ flex:1,fontSize:12,color:T.text,fontFamily:"'DM Sans',sans-serif" }}>{n}</div>
              <div style={{ fontSize:9,padding:"2px 7px",borderRadius:4,
                background: s==="checked-in" ? `${T.blue}20` : `${T.muted}20`,
                color: s==="checked-in" ? "#60a5fa" : T.muted }}>
                {s}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── STATS BAR ──────────────────────────────────────────── */
function StatsBar() {
  const stats = [
    { label: "clinics active", value: 800, prefix: "" },
    { label: "no-show reduction", value: 40, prefix: "", suffix: "%" },
    { label: "months free trial", value: 1, prefix: "", suffix: "" },
    { label: "minutes to set up", value: 60, prefix: "<", suffix: "" },
  ];
  return (
    <motion.div initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.2}} transition={{duration:.7,ease:[.22,1,.36,1]}} className="stats-section" style={{ borderTop:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}`,margin:"0 0",padding:"40px 24px" }}>
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map(s=>(
          <div key={s.label}>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
              fontSize:"clamp(32px,4vw,48px)",letterSpacing:"-0.03em",color:T.text }}>
              <CountUp to={s.value} prefix={s.prefix} suffix={s.suffix}/>
            </div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.muted,marginTop:4 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* ─── FEATURES — typographic list, not card grid ─────────── */
const FEATURES = [
  { icon: Calendar, label: "Appointment scheduling", desc: "Multi-doctor calendar, walk-in queue, smart slots. Patients book online from your microsite.", accent: T.blue },
  { icon: Users, label: "Patient records", desc: "Searchable history, visit notes, prescriptions, and follow-up tracking — all linked together.", accent: "#6366F1" },
  { icon: Receipt, label: "Billing and payments", desc: "GST invoices in one click. Cash, UPI, card, or bank transfer. Instant WhatsApp delivery.", accent: "#10B981" },
  { icon: FileText, label: "Digital prescriptions", desc: "Generate, sign, and share Rx as PDF or DOCX. Linked to every patient visit automatically.", accent: "#F59E0B" },
  { icon: MessageCircle, label: "WhatsApp reminders", desc: "Confirmations, reminders, and invoices sent directly — no SMS fees, no third-party apps.", accent: "#22C55E" },
  { icon: Globe, label: "Clinic microsite", desc: "Free online presence at yourname.hedesk.help with live appointment booking built in.", accent: "#A78BFA" },
  { icon: BarChart3, label: "Revenue analytics", desc: "Footfall, revenue trends, busiest hours — visible every morning on your dashboard.", accent: T.amber },
  { icon: ShieldCheck, label: "Staff management", desc: "Role-based access for doctors, receptionists, and admin. Everyone sees exactly what they need.", accent: "#FB923C" },
];

function Features() {
  return (
    <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} id="features" style={{ padding:"96px 24px" }}>
      <div className="max-w-6xl mx-auto">
        {/* Header — left aligned, not centered */}
        <div style={{ maxWidth:560,marginBottom:64 }}>
          <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:16 }}>
            <div style={{ width:28,height:1,background:T.blue }}/>
            <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.blue,fontWeight:500 }}>
              What's inside
            </span>
          </div>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
            fontSize:"clamp(32px,4vw,48px)",letterSpacing:"-0.02em",color:T.text,lineHeight:1.1 }}>
            Everything your clinic runs on, finally in one place.
          </h2>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:16,color:T.sub,lineHeight:1.7,marginTop:16 }}>
            Built from the ground up for Indian clinics — from a 1-doctor practice to a multi-specialty centre.
          </p>
        </div>

        {/* Feature list — two columns of large list items */}
        <div className="features-grid" style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(380px,1fr))",gap:"0" }}>
          {FEATURES.map((f,i)=>(
            <Reveal key={f.label} delay={i * 0.055} y={20} className="feature-reveal">
            <div style={{
              display:"flex",alignItems:"flex-start",gap:16,
              padding:"24px 0",
              borderTop:`1px solid ${T.border}`,
              borderRight: i%2===0 ? `1px solid ${T.border}` : "none",
              paddingRight: i%2===0 ? 40 : 0,
              paddingLeft: i%2===1 ? 40 : 0,
            }}>
              <div style={{ width:40,height:40,borderRadius:10,background:`${f.accent}15`,
                border:`1px solid ${f.accent}30`,display:"flex",alignItems:"center",justifyContent:"center",
                flexShrink:0,marginTop:2 }}>
                <f.icon size={18} color={f.accent}/>
              </div>
              <div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:700,
                  fontSize:16,color:T.text,marginBottom:5 }}>{f.label}</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,color:T.sub,lineHeight:1.6 }}>
                  {f.desc}
                </div>
              </div>
            </div>
            </Reveal>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

/* ─── MICROSITE ──────────────────────────────────────────── */
function Microsite() {
  return (
    <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} id="microsite" style={{ padding:"80px 24px",background:T.surface,borderTop:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}` }}>
      <Reveal className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:16 }}>
            <div style={{ width:28,height:1,background:T.blue }}/>
            <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.blue,fontWeight:500 }}>
              Online presence
            </span>
          </div>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
            fontSize:"clamp(28px,4vw,44px)",letterSpacing:"-0.02em",color:T.text,lineHeight:1.1,marginBottom:16 }}>
            A free microsite for every clinic.
          </h2>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:16,color:T.sub,lineHeight:1.7,marginBottom:24 }}>
            Your patients can find you, learn about your practice, and book an appointment — all from a custom subdomain. No website needed.
          </p>
          {["Live appointment booking","Your clinic details and hours","Shareable link for WhatsApp and social"].map(f=>(
            <div key={f} style={{ display:"flex",alignItems:"center",gap:10,marginBottom:12 }}>
              <CheckCircle2 size={16} color={T.blue}/>
              <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,color:T.sub }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Microsite mockup */}
        <div className="glass-card" style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:24,overflow:"hidden",boxShadow:`0 24px 48px -12px rgba(0,0,0,0.5)` }}>
          <div style={{ padding:"10px 14px",borderBottom:`1px solid ${T.border}`,background:T.surface,display:"flex",alignItems:"center",gap:7 }}>
            <div style={{ width:9,height:9,borderRadius:"50%",background:"#ef4444",opacity:0.7 }}/>
            <div style={{ width:9,height:9,borderRadius:"50%",background:"#f59e0b",opacity:0.7 }}/>
            <div style={{ width:9,height:9,borderRadius:"50%",background:"#10b981",opacity:0.7 }}/>
            <div style={{ marginLeft:8,display:"flex",alignItems:"center",gap:5,padding:"3px 10px",background:"rgba(255,255,255,0.05)",borderRadius:5 }}>
              <ShieldCheck size={10} color="#10b981"/>
              <span style={{ fontFamily:"monospace",fontSize:11,color:T.muted }}>sanathoi.hedesk.help</span>
            </div>
          </div>
          <div style={{ padding:24 }}>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:T.blue,marginBottom:6,fontWeight:500 }}>
              Sanathoi Care Clinic
            </div>
            <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,fontSize:22,color:T.text,lineHeight:1.2,marginBottom:8 }}>
              Compassionate family care in Imphal.
            </div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.muted,marginBottom:20 }}>
              MG Road · Open Mon–Sat · Dr. Anjali Mehta
            </div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:T.muted,marginBottom:10,fontWeight:500 }}>
              Choose a slot
            </div>
            {["Tomorrow · 10:00 AM","Tomorrow · 10:30 AM","Friday 14 · 09:00 AM"].map((s,i)=>(
              <div key={s} style={{
                display:"flex",justifyContent:"space-between",alignItems:"center",
                padding:"10px 14px",borderRadius:8,marginBottom:6,
                background: i===0 ? `${T.blue}20` : "rgba(255,255,255,0.03)",
                border: `1px solid ${i===0 ? T.blue+"50" : T.border}`,
                fontFamily:"'DM Sans',sans-serif",fontSize:13,
                color: i===0 ? T.text : T.muted,
              }}>
                <span>{s}</span>
                <span style={{ fontSize:11,color: i===0 ? T.blue : T.muted }}>
                  {i===0 ? "Selected" : "Available"}
                </span>
              </div>
            ))}
            <LiquidGlassButton variant="primary" className="full-glass-button">
              Confirm booking <ArrowRight size={15}/>
            </LiquidGlassButton>
          </div>
        </div>
      </Reveal>
    </motion.section>
  );
}

/* ─── WORKFLOW ───────────────────────────────────────────── */
const STEPS = [
  { icon: Globe, label: "Patient books online" },
  { icon: Calendar, label: "Appointment created" },
  { icon: Users, label: "Patient arrives" },
  { icon: Stethoscope, label: "Doctor consults" },
  { icon: FileText, label: "Prescription generated" },
  { icon: Receipt, label: "Invoice sent via WhatsApp" },
  { icon: Clock, label: "Follow-up scheduled" },
];

function Workflow() {
  return (
    <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} style={{ padding:"96px 24px" }}>
      <div className="max-w-4xl mx-auto">
        <div style={{ maxWidth:500,marginBottom:56 }}>
          <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:16 }}>
            <div style={{ width:28,height:1,background:T.blue }}/>
            <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.blue,fontWeight:500 }}>
              How it works
            </span>
          </div>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
            fontSize:"clamp(28px,4vw,44px)",letterSpacing:"-0.02em",color:T.text,lineHeight:1.1 }}>
            Seven steps. Zero paperwork.
          </h2>
        </div>
        <div style={{ position:"relative" }}>
          {/* vertical line */}
          <div style={{ position:"absolute",left:19,top:0,bottom:0,width:1,background:`linear-gradient(to bottom, ${T.blue}, ${T.dim})` }}/>
          {STEPS.map((s,i)=>(
            <Reveal key={s.label} delay={i * 0.06} y={18} className="workflow-reveal">
            <div key={s.label} style={{ display:"flex",alignItems:"center",gap:20,paddingLeft:0,marginBottom:i<STEPS.length-1?0:0 }}>
              <div style={{ width:40,height:40,borderRadius:"50%",flexShrink:0,zIndex:1,
                background: i<3 ? T.blue : i<5 ? "#6366F1" : "#10B981",
                display:"flex",alignItems:"center",justifyContent:"center",
                boxShadow:`0 0 0 4px ${T.bg}` }}>
                <s.icon size={16} color="#fff"/>
              </div>
              <div style={{ flex:1,padding:"16px 20px",borderRadius:10,
                background: "transparent",
                borderBottom: i<STEPS.length-1 ? `1px solid ${T.border}` : "none" }}>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:600,
                  fontSize:16,color:T.text }}>
                  {s.label}
                </div>
              </div>
            </div>
            </Reveal>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

/* ─── TESTIMONIALS ───────────────────────────────────────── */
const REVIEWS = [
  { name:"Dr. Anjali Mehta", role:"GP · Imphal", quote:"I shut down my Excel sheets the day we switched. Patient checkout went from 12 minutes to 90 seconds." },
  { name:"Dr. Rohan Patel", role:"Pediatrician · Pune", quote:"WhatsApp reminders cut my no-show rate by 40%. I'd pay for that feature alone." },
  { name:"Sneha Iyer", role:"Receptionist · Bangalore", quote:"The walk-in queue is genius. Patients see their token, doctors see who's next." },
  { name:"Dr. Karthik N.", role:"Dermatologist · Chennai", quote:"Online booking brings 30+ new patients a month. Better than any ad spend." },
  { name:"Dr. Priya Sharma", role:"Multi-specialty · Delhi", quote:"HeDesk handles 4 doctors and 3 receptionists without breaking a sweat." },
  { name:"Dr. Mohan Reddy", role:"Cardiologist · Hyderabad", quote:"Invoice + Rx sent on WhatsApp in under a minute. Patients genuinely love it." },
];

function Testimonials() {
  return (
    <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} id="reviews" style={{ padding:"80px 0",background:T.surface,borderTop:`1px solid ${T.border}` }}>
      <div style={{ maxWidth:1152,margin:"0 auto",paddingLeft:24 }}>
        <div style={{ maxWidth:480,marginBottom:48 }}>
          <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:16 }}>
            <div style={{ width:28,height:1,background:T.blue }}/>
            <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.blue,fontWeight:500 }}>
              Loved by doctors
            </span>
          </div>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
            fontSize:"clamp(28px,4vw,44px)",letterSpacing:"-0.02em",color:T.text,lineHeight:1.1 }}>
            Built with clinics across India.
          </h2>
        </div>
      </div>
      {/* Scrolling marquee */}
      <div style={{ overflow:"hidden",maskImage:"linear-gradient(to right, transparent, black 6%, black 94%, transparent)" }}>
        <div style={{ display:"flex",gap:16,animation:"marquee 32s linear infinite",width:"max-content" }}>
          {[...REVIEWS,...REVIEWS].map((r,i)=>(
            <div key={i} style={{ width:300,flexShrink:0,padding:24,
              background:T.card,border:`1px solid ${T.border}`,borderRadius:16 }}>
              <div style={{ display:"flex",gap:2,marginBottom:12 }}>
                {[1,2,3,4,5].map(j=><Star key={j} size={13} fill={T.amber} color={T.amber}/>)}
              </div>
              <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:14,color:T.sub,lineHeight:1.7,marginBottom:20 }}>
                "{r.quote}"
              </p>
              <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:14 }}>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:700,fontSize:14,color:T.text }}>{r.name}</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:T.muted,marginTop:2 }}>{r.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }`}</style>
    </motion.section>
  );
}

/* ─── PRICING ────────────────────────────────────────────── */
const PLANS = [
  {
    name:"Basic", price:"₹699", sub:"/month",
    desc:"A single-doctor practice getting started.",
    features:["1 doctor + 1 receptionist","500 patients","Online booking microsite","WhatsApp reminders","PDF invoices"],
    cta:"Start free trial",
  },
  {
    name:"Pro", price:"₹899", sub:"/month",
    desc:"Growing clinics that need to run on autopilot.",
    features:["Unlimited doctors and staff","Unlimited patients","Custom subdomain","Razorpay payments","DOCX exports","Priority support"],
    cta:"Start free trial",
    featured:true,
  },
  {
    name:"Enterprise", price:"Coming soon", sub:"",
    desc:"Multi-location chains with custom workflows.",
    features:["Multi-location dashboard","Patient mobile app API","Custom roles and audit logs","Advanced reports","Dedicated success manager"],
    cta:"Get notified",
    soon:true,
  },
];

function Pricing() {
  return (
    <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} id="pricing" style={{ padding:"96px 24px" }}>
      <div className="max-w-5xl mx-auto">
        <div style={{ maxWidth:480,marginBottom:56 }}>
          <div style={{ display:"flex",alignItems:"center",gap:12,marginBottom:16 }}>
            <div style={{ width:28,height:1,background:T.blue }}/>
            <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.blue,fontWeight:500 }}>
              Pricing
            </span>
          </div>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
            fontSize:"clamp(28px,4vw,44px)",letterSpacing:"-0.02em",color:T.text,lineHeight:1.1 }}>
            Honest pricing. No per-patient fees.
          </h2>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:15,color:T.sub,lineHeight:1.7,marginTop:12 }}>
            Cancel any time. No contracts. 30 days free, no card required.
          </p>
        </div>

        <div className="pricing-grid" style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16 }}>
          {PLANS.map((p,i)=>(
            <Reveal key={p.name} delay={i * 0.08} y={24} className="pricing-reveal">
            <div style={{
              background: p.featured ? T.blue : T.card,
              border: `1px solid ${p.featured ? T.blue : p.soon ? T.border : T.border}`,
              borderRadius:18,padding:28,display:"flex",flexDirection:"column",
              boxShadow: p.featured ? `0 20px 48px -8px ${T.blue}50` : "none",
              borderStyle: p.soon ? "dashed" : "solid",
            }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12 }}>
                <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:700,
                  fontSize:15,color: p.featured?"#fff":T.text }}>{p.name}</span>
                {p.featured && <span style={{ fontSize:10,fontWeight:700,
                  background:"rgba(255,255,255,0.2)",color:"#fff",padding:"3px 9px",borderRadius:20,
                  fontFamily:"'DM Sans',sans-serif",letterSpacing:"0.05em" }}>RECOMMENDED</span>}
                {p.soon && <span style={{ fontSize:10,fontWeight:700,
                  background:`${T.amber}20`,color:T.amber,padding:"3px 9px",borderRadius:20,
                  fontFamily:"'DM Sans',sans-serif" }}>Soon</span>}
              </div>

              <div style={{ marginBottom:8 }}>
                {p.soon ? (
                  <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,fontSize:22,color:T.sub }}>Coming soon</span>
                ) : (
                  <>
                    <span style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
                      fontSize:40,letterSpacing:"-0.02em",color:p.featured?"#fff":T.text }}>{p.price}</span>
                    <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,
                      color:p.featured?"rgba(255,255,255,0.6)":T.muted,marginLeft:4 }}>{p.sub}</span>
                  </>
                )}
              </div>

              <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,
                color:p.featured?"rgba(255,255,255,0.7)":T.sub,marginBottom:24,lineHeight:1.6 }}>{p.desc}</p>

              <div style={{ flex:1,marginBottom:24 }}>
                {p.features.map(f=>(
                  <div key={f} style={{ display:"flex",alignItems:"flex-start",gap:8,marginBottom:10 }}>
                    <CheckCircle2 size={14} color={p.featured?"rgba(255,255,255,0.8)":T.blue} style={{ marginTop:2,flexShrink:0 }}/>
                    <span style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,
                      color:p.featured?"rgba(255,255,255,0.85)":T.sub }}>{f}</span>
                  </div>
                ))}
              </div>

              {p.soon ? (
                <LiquidGlassButton variant="secondary" className="full-glass-button" disabled>
                  {p.cta}
                </LiquidGlassButton>
              ) : (
                <LiquidGlassButton
                  to="/signup"
                  variant={p.featured ? "primary" : "secondary"}
                  className="full-glass-button"
                >
                  {p.cta}
                </LiquidGlassButton>
              )}
            </div>
            </Reveal>
          ))}
        </div>
      </div>
    </motion.section>
  );
}

/* ─── FINAL CTA ──────────────────────────────────────────── */
function FinalCTA() {
  return (
    <motion.section initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} style={{ padding:"80px 24px",background:T.surface,borderTop:`1px solid ${T.border}` }}>
      <div className="max-w-5xl mx-auto">
        <motion.div whileHover={{y:-4}} transition={{type:"spring",stiffness:300,damping:24}} className="final-cta-card" style={{ background:`linear-gradient(135deg, ${T.blue}30, #6366F120)`,
          border:`1px solid ${T.blue}40`,borderRadius:24,padding:"64px 48px",
          textAlign:"center",position:"relative",overflow:"hidden" }}>
          <div style={{ position:"absolute",inset:"-50%",
            background:`radial-gradient(ellipse at 50% 0%, ${T.blue}20, transparent 60%)`,
            pointerEvents:"none" }}/>
          <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,
            fontSize:"clamp(28px,4vw,48px)",letterSpacing:"-0.02em",color:T.text,
            lineHeight:1.1,marginBottom:16,position:"relative" }}>
            Ready to modernize your clinic?
          </h2>
          <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:16,color:T.sub,
            maxWidth:480,margin:"0 auto 32px",lineHeight:1.7,position:"relative" }}>
            Join 80+ practices across India. Start free in two minutes — no credit card, no commitment.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 relative">
            <LiquidGlassButton to="/signup" icon={<ArrowRight size={16}/>}>Start free trial</LiquidGlassButton>
            <LiquidGlassButton href="mailto:sanasamhembasingh@gmail.com" variant="secondary">Schedule demo</LiquidGlassButton>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}

/* ─── FOOTER ─────────────────────────────────────────────── */
function Footer() {
  return (
    <footer
      style={{
        borderTop: `1px solid ${T.border}`,
        background: T.bg,
      }}
      className="px-5 sm:px-6 pt-12 sm:pt-14 pb-8"
    >
      <div className="max-w-6xl mx-auto">

        {/* Main Footer Grid */}
        <div
          className="
            grid
            grid-cols-3
            gap-x-5
            gap-y-10
            lg:grid-cols-[2fr_1fr_1fr_1fr]
            lg:gap-10
            mb-12
          "
        >

          {/* ───────── BRAND ───────── */}
          <div className="col-span-3 lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-3.5">
              <Logo size={26} />

              <span
                className="font-bold text-base"
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: T.text,
                }}
              >
                HeDesk
              </span>
            </div>

            <p
              className="text-[13px] leading-7 max-w-[260px]"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: T.muted,
              }}
            >
              The all-in-one clinic management platform for Manipuri
              doctors and receptionists.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-2.5 mt-5">
              {[Twitter, Linkedin, Github].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label={
                    i === 0
                      ? "Twitter"
                      : i === 1
                        ? "LinkedIn"
                        : "GitHub"
                  }
                  className="
                    w-8 h-8
                    rounded-lg
                    flex items-center justify-center
                    transition-all duration-200
                    hover:text-white
                    hover:-translate-y-0.5
                  "
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: `1px solid ${T.border}`,
                    color: T.muted,
                  }}
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* ───────── PRODUCT ───────── */}
          <div>
            <div
              className="font-semibold text-[13px] mb-3.5"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                color: T.text,
              }}
            >
              Product
            </div>

            {[
              ["#features", "Features"],
              ["#pricing", "Pricing"],
              ["#reviews", "Reviews"],
            ].map(([href, label]) => (
              <div key={label} className="mb-2.5">
                <a
                  href={href}
                  className="text-[13px] transition-colors hover:text-white"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: T.muted,
                  }}
                >
                  {label}
                </a>
              </div>
            ))}
          </div>

          {/* ───────── COMPANY ───────── */}
          <div>
            <div
              className="font-semibold text-[13px] mb-3.5"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                color: T.text,
              }}
            >
              Company
            </div>

            {[
              ["/blog", "Blog"],
              ["/privacy", "Privacy"],
              ["/terms", "Terms"],
            ].map(([href, label]) => (
              <div key={label} className="mb-2.5">
                <a
                  href={href}
                  className="text-[13px] transition-colors hover:text-white"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: T.muted,
                  }}
                >
                  {label}
                </a>
              </div>
            ))}
          </div>

          {/* ───────── CONTACT ───────── */}
          <div>
            <div
              className="font-semibold text-[13px] mb-3.5"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                color: T.text,
              }}
            >
              Contact
            </div>

            <div className="mb-2.5">
              <a
                href="mailto:hello@hedesk.help"
                className="
                  text-[13px]
                  transition-colors
                  hover:text-white
                  break-words
                "
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: T.muted,
                }}
              >
                hello@hedesk.help
              </a>
            </div>

            <div>
              <a
                href="/login"
                className="text-[13px] transition-colors hover:text-white"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: T.muted,
                }}
              >
                Sign in
              </a>
            </div>
          </div>
        </div>

        {/* ───────── BOTTOM BAR ───────── */}
        <div
          className="
            border-t
            pt-5
            flex
            flex-col
            sm:flex-row
            sm:items-center
            sm:justify-between
            gap-3
          "
          style={{
            borderColor: T.border,
          }}
        >
          <span
            className="text-[12px]"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: T.muted,
            }}
          >
            © {new Date().getFullYear()} HeDesk. Built with care for
            Manipur clinics.
          </span>

          <span
            className="text-[12px]"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: T.muted,
            }}
          >
            Made in Manipur · For India
          </span>
        </div>
        </div>
    </footer>
  );
}


/* ─── LIQUID GLASS SYSTEM ───────────────────────────────── */
const GLASS_CSS = `
  :root { color-scheme: light; }
  .scroll-progress { position: fixed; top: 0; left: 0; right: 0; height: 2px; z-index: 100; transform-origin: 0% 50%; background: linear-gradient(90deg, #1769E8, #6EA8FF, #1769E8); box-shadow: 0 0 14px rgba(23,105,232,.45); }
  body {
    margin: 0;
    background: #eef5ff;
    color: ${T.text};
    font-family: 'DM Sans', sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  ::selection { background: rgba(23,105,232,.18); color: #10213D; }
  .glass-surface {
    background: rgba(255,255,255,.56) !important;
    border: 1px solid rgba(255,255,255,.76) !important;
    box-shadow: 0 18px 55px rgba(40,76,120,.09), inset 0 1px 0 rgba(255,255,255,.9) !important;
    backdrop-filter: blur(24px) saturate(145%);
    -webkit-backdrop-filter: blur(24px) saturate(145%);
  }
  .glass-card {
    background: rgba(255,255,255,.64) !important;
    border: 1px solid rgba(255,255,255,.82) !important;
    box-shadow: 0 24px 70px rgba(46,81,125,.10), inset 0 1px 0 rgba(255,255,255,.92) !important;
    backdrop-filter: blur(26px) saturate(150%);
    -webkit-backdrop-filter: blur(26px) saturate(150%);
  }
  .liquid-button {
    position: relative;
    isolation: isolate;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    padding: 0.8rem 1.35rem;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,.78);
    color: ${T.text};
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -.01em;
    cursor: pointer;
    overflow: hidden;
    background: linear-gradient(135deg, rgba(255,255,255,.78), rgba(255,255,255,.42));
    box-shadow: 0 14px 35px rgba(57,94,145,.14), inset 0 1px 0 rgba(255,255,255,.95), inset 0 -1px 0 rgba(255,255,255,.28);
    backdrop-filter: blur(20px) saturate(160%);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
  }
  .liquid-button-secondary {
    color: ${T.text};
    background: rgba(255,255,255,.46);
    border-color: rgba(255,255,255,.8);
  }
  .liquid-button-primary {
    color: #fff;
    border-color: rgba(255,255,255,.38);
    background: linear-gradient(135deg, rgba(23,105,232,.92), rgba(67,139,245,.72));
    box-shadow: 0 16px 40px rgba(23,105,232,.24), inset 0 1px 0 rgba(255,255,255,.45), inset 0 -1px 0 rgba(255,255,255,.12);
  }
  .liquid-button-content { position: relative; z-index: 2; display: inline-flex; align-items: center; justify-content: center; gap: .55rem; }
  .liquid-button-shine {
    position: absolute; inset: -80% -30%; z-index: 1;
    background: linear-gradient(115deg, transparent 35%, rgba(255,255,255,.34) 48%, transparent 61%);
    transform: translateX(-65%) rotate(8deg);
    transition: transform .7s cubic-bezier(.22,1,.36,1);
    pointer-events: none;
  }
  .liquid-button:hover .liquid-button-shine { transform: translateX(65%) rotate(8deg); }
  .liquid-button:focus-visible { outline: 3px solid rgba(23,105,232,.25); outline-offset: 4px; }
  .liquid-link {
    display: inline-flex; align-items: center; gap: .4rem; min-height: 48px;
    padding: .8rem 1rem; border-radius: 999px; color: ${T.sub};
    border: 1px solid rgba(255,255,255,.58); background: rgba(255,255,255,.28);
    backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
    transition: transform .25s ease, background .25s ease, color .25s ease;
  }
  .liquid-link:hover { transform: translateY(-2px); color: ${T.text}; background: rgba(255,255,255,.5); }
  .nav-button { min-height: 40px; padding: .55rem 1rem; font-size: 13px; }
  .glass-button {
    box-shadow: 0 12px 30px rgba(23,105,232,.20), inset 0 1px 0 rgba(255,255,255,.25) !important;
    transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
  }
  .glass-button:hover { transform: translateY(-2px); box-shadow: 0 18px 38px rgba(23,105,232,.24), inset 0 1px 0 rgba(255,255,255,.3) !important; }
  .glass-button:active { transform: translateY(0) scale(.98); }
  .float-soft { animation: floatSoft 7s ease-in-out infinite; }
  @keyframes floatSoft { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

  .mobile-menu-trigger { display: none; width: 42px; height: 42px; padding: 0; align-items: center; justify-content: center; }
  .menu-icon { width: 18px; height: 14px; position: relative; display: block; }
  .menu-icon i { position: absolute; left: 0; width: 18px; height: 1.5px; border-radius: 2px; background: ${T.text}; transition: transform .22s ease, top .22s ease; }
  .menu-icon i:first-child { top: 4px; }
  .menu-icon i:last-child { top: 10px; }
  .menu-icon.is-open i:first-child { top: 7px; transform: rotate(45deg); }
  .menu-icon.is-open i:last-child { top: 7px; transform: rotate(-45deg); }
  .mobile-menu { display: none; margin: .5rem 1rem 0; padding: .65rem; border: 1px solid rgba(255,255,255,.55); border-radius: 1.25rem; background: rgba(255,255,255,.58); backdrop-filter: blur(24px) saturate(170%); -webkit-backdrop-filter: blur(24px) saturate(170%); box-shadow: 0 20px 60px rgba(34,76,130,.16), inset 0 1px 0 rgba(255,255,255,.72); overflow: hidden; }
  .mobile-menu a { display: flex; align-items: center; min-height: 46px; padding: .7rem .85rem; border-radius: .8rem; color: ${T.text}; font-family: 'DM Sans',sans-serif; font-size: 14px; text-decoration: none; }
  .mobile-menu a:hover { background: rgba(255,255,255,.5); }
  .mobile-menu-cta { width: 100% !important; margin-top: .35rem; }
  .feature-reveal, .workflow-reveal, .pricing-reveal { min-width: 0; }
  @media (hover: none) { .liquid-button:hover .liquid-button-shine { transform: translateX(-65%) rotate(8deg); } }

  .full-glass-button { width:100%; }
  .full-glass-button.liquid-button { min-height:46px; }
  .liquid-button:disabled { opacity:.58; cursor:not-allowed; transform:none !important; filter:saturate(.65); }
  .liquid-button:disabled .liquid-button-shine { display:none; }
  /* Responsive liquid-glass layout */
  html { overflow-x: hidden; scroll-behavior: smooth; }
  body { overflow-x: hidden; }
  .hero-section {
  position: relative;
  isolation: isolate;
  min-height: 790px;
  padding-top: 138px;
  background: #fff;
  overflow: visible;
}

/* Soft white → light blue hero background */
.hero-section::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;

  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      rgba(255,255,255,.98) 38%,
      rgba(246,251,255,.96) 72%,
      rgba(232,244,255,.92) 100%
    ),
    radial-gradient(
      circle at 50% 18%,
      rgba(173,219,255,.28),
      transparent 55%
    );
}

/* Square grid */
.hero-section::after {
  content: "";
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  background-image:
    linear-gradient(rgba(23,105,232,.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(23,105,232,.08) 1px, transparent 1px);

  background-size: 72px 72px;

  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 12%,
    black 65%,
    transparent 100%
  );

  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 12%,
    black 65%,
    transparent 100%
  );

  z-index: 1;
  pointer-events: none;
}

  /* Fade grid toward bottom */
  mask-image:
    linear-gradient(
      to bottom,
      black 0%,
      black 58%,
      rgba(0,0,0,.65) 72%,
      transparent 100%
    );

  -webkit-mask-image:
    linear-gradient(
      to bottom,
      black 0%,
      black 58%,
      rgba(0,0,0,.65) 72%,
      transparent 100%
    );
}

/* Light blue square illumination */
.hero-square-glow {
  position: absolute;

  width: min(72vw, 920px);
  height: min(72vw, 920px);

  left: 50%;
  top: 38px;

  transform: translateX(-50%);

  border-radius: 42px;

  z-index: 0;
  pointer-events: none;

  background:
    radial-gradient(
      circle at 50% 18%,
      rgba(170,218,255,.38),
      transparent 52%
    ),
    radial-gradient(
      circle at 50% 55%,
      rgba(220,240,255,.42),
      transparent 70%
    );

  box-shadow:
    inset 0 0 100px rgba(255,255,255,.9),
    0 30px 100px rgba(61,130,205,.08);
}

/* Hero content must sit above grid */
.hero-inner {
  position: relative;
  z-index: 2;
  text-align: center;
  padding-bottom: 150px;
}

.hero-copy {
  max-width: 850px;
  margin: 0 auto;
  position: relative;
  z-index: 3;
}

.hero-dashboard {
  position: relative;
  z-index: 5;
}
  .hero-inner { position: relative; text-align: center; padding-bottom: 150px; }
  .hero-copy { max-width: 850px; margin: 0 auto; position: relative; z-index: 2; }
  .hero-eyebrow { display:flex; justify-content:center; align-items:center; gap:12px; margin-bottom:24px; color:${T.blue}; font:500 13px 'DM Sans',sans-serif; }
  .hero-eyebrow-line { width:32px; height:1px; background:${T.blue}; opacity:.65; }
  .hero-copy h1 { margin:0; font:800 clamp(46px,7vw,82px)/1.02 'Plus Jakarta Sans',sans-serif; letter-spacing:-.055em; color:${T.text}; }
  .hero-copy h1 span { color:${T.blue}; }
  .hero-script { font-family: 'Segoe Script', 'Brush Script MT', 'Snell Roundhand', cursive; font-weight: 600; font-style: italic; letter-spacing: -.035em; position: relative; display: inline-block; padding: 0 .06em .06em; line-height: .96; }
  .hero-script::after { content: ''; position:absolute; left: 3%; right: 1%; bottom: .02em; height: .08em; border-radius: 999px; background: linear-gradient(90deg, rgba(23,105,232,.16), rgba(23,105,232,.48), rgba(23,105,232,.08)); transform: rotate(-1.5deg); transform-origin: left center; }
  .hero-copy p { max-width:640px; margin:24px auto 0; color:${T.sub}; font:400 17px/1.7 'DM Sans',sans-serif; }
  .hero-actions { display:flex; justify-content:center; align-items:center; flex-wrap:wrap; gap:12px; margin-top:32px; }
  .hero-proof { display:flex; justify-content:center; align-items:center; gap:12px; margin-top:20px; }
  .proof-avatars { display:flex; padding-left:8px; }
  .proof-avatars span { width:29px; height:29px; margin-left:-8px; border:2px solid #fff; border-radius:50%; box-shadow:0 4px 14px rgba(38,78,124,.12); }
  .hero-proof strong, .hero-proof small { display:block; text-align:left; }
  .hero-proof strong { color:${T.text}; font:700 13px 'Plus Jakarta Sans',sans-serif; }
  .hero-proof small { color:${T.muted}; font:400 12px 'DM Sans',sans-serif; margin-top:2px; }
  .hero-dashboard { position:relative; width:min(100%, 920px); margin:72px auto -150px; z-index:5; filter:drop-shadow(0 35px 55px rgba(41,82,130,.15)); }
  .dashboard-floor-glow { position:absolute; width:70%; height:100px; left:15%; bottom:-45px; background:rgba(91,164,238,.22); filter:blur(40px); z-index:-1; }
  .hero-dashboard .glass-card { box-shadow:0 28px 90px rgba(34,78,127,.16), inset 0 1px 0 rgba(255,255,255,.95) !important; }
  .glass-card { min-width:0; }
  .features-grid, .pricing-grid { min-width:0; }
  .liquid-button { will-change:transform; touch-action:manipulation; }
  .liquid-button:active { filter:brightness(.98); }
  .content-section { scroll-margin-top:92px; }
  @media (max-width: 1023px) {
    .hero-section { min-height: 700px; padding-top:125px; }
    .hero-copy h1 { font-size:clamp(44px,8vw,68px); }
    .hero-dashboard { width:min(94vw,820px); margin-top:58px; margin-bottom:-130px; }
  }
  @media (max-width: 767px) {
    .mobile-menu-trigger { display:inline-flex; }
    .mobile-menu { display:flex; flex-direction:column; }
    .hero-section { min-height:720px; padding:104px .875rem 0 !important; overflow:visible; }
    .hero-square-glow { width:92vw; height:92vw; top:30px; border-radius:28px; }
    .hero-section::after { width:92vw; height:92vw; top:30px; border-radius:28px; background-size:34px 34px; }
    .hero-copy { max-width:36rem; }
    .hero-eyebrow { margin-bottom:20px !important; }
    .hero-eyebrow-line { width:22px; }
    .hero-copy h1 { font-size:clamp(2.65rem,12vw,4rem) !important; line-height:1.015 !important; letter-spacing:-.055em !important; }
    .hero-copy p { max-width:34rem !important; font-size:15px !important; line-height:1.65 !important; margin-top:20px !important; }
    .hero-actions { flex-direction:column; width:100%; margin-top:26px; }
    .hero-actions > *, .hero-actions .liquid-button, .hero-actions .liquid-link { width:min(100%,21rem); }
    .hero-actions .liquid-link { justify-content:center; }
    .hero-proof { margin-top:18px; }
    .hero-dashboard { width:calc(100vw - 28px); max-width:760px; margin-top:42px; margin-bottom:-110px; }
    .hero-dashboard > div { border-radius:20px !important; }
    .stats-section { padding-top:150px !important; }
    .stats-section .max-w-6xl { grid-template-columns:repeat(2,minmax(0,1fr)) !important; gap:1.5rem 1rem !important; }
    .stats-section .max-w-6xl > div { text-align:center; }
    .stats-section .max-w-6xl > div > div:first-child { font-size:2.2rem !important; }
    .features-section, .pricing-section { padding:4.5rem .875rem !important; }
    .features-grid { grid-template-columns:1fr !important; }
    .features-grid > div { padding:1.1rem 0 !important; border-right:0 !important; padding-left:0 !important; padding-right:0 !important; }
    .microsite-section { padding:4rem 1rem !important; }
    .workflow-section { padding:4.5rem 1rem !important; }
    .testimonials-section { padding:4rem 0 !important; }
    .pricing-grid { grid-template-columns:1fr !important; gap:1rem !important; }
    .final-cta-card { padding:2.75rem 1.25rem !important; border-radius:1.5rem !important; }
    .final-cta-card .flex { flex-direction:column; width:100%; }
    .final-cta-card .liquid-button { width:min(100%,20rem); }
    footer { padding-left:.875rem !important; padding-right:.875rem !important; }
    .content-section { scroll-margin-top:76px; }
    .liquid-button { min-height:50px; padding-inline:1.25rem; }
  }
  @media (max-width: 420px) {
    .hero-section { min-height:690px; padding-top:96px !important; }
    .hero-square-glow { width:94vw; height:94vw; border-radius:24px; }
    .hero-section::after { width:94vw; height:94vw; top:30px; border-radius:24px; background-size:30px 30px; }
    .hero-eyebrow span { font-size:12px !important; }
    .hero-copy h1 { font-size:2.42rem !important; }
    .hero-copy p { font-size:14px !important; }
    .hero-dashboard { width:calc(100vw - 20px); margin-top:34px; margin-bottom:-96px; }
    .stats-section { padding-top:132px !important; }
    .stats-section .max-w-6xl { gap:1.25rem .5rem !important; }
    .stats-section .max-w-6xl > div > div:first-child { font-size:1.9rem !important; }
  }
  @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: .01ms !important; animation-iteration-count: 1 !important; scroll-behavior: auto !important; transition-duration: .01ms !important; } }
`;

/* ─── ROOT ───────────────────────────────────────────────── */
export default function Landing() {
  useEffect(()=>{ document.documentElement.style.scrollBehavior="smooth"; return()=>{ document.documentElement.style.scrollBehavior=""; }; },[]);
  return (
    <div style={{ minHeight:"100vh",color:T.text }} data-testid="landing-root">
      <style>{GLASS_CSS}</style>
      <PageMeta
        title="HeDesk — Clinic Management Software for Indian Doctors"
        description="HeDesk is an all-in-one clinic management platform for Indian doctors: online booking, appointments, prescriptions, billing, WhatsApp reminders. Start free."
        canonical="https://hedesk.help/"
      />
      <Background/>
      <ScrollProgress/>
      <Navbar/>
      <Hero/>
      <StatsBar/>
      <Features/>
      <Workflow/>
      <Microsite/>
      <Testimonials/>
      <Pricing/>
      <FinalCTA/>
      <Footer/>
    </div>
  );
}